// Third-party imports
import React from 'react';
import { AccessDenied, Admin, Authenticated, CustomRoutes, Resource, CanAccess } from 'react-admin';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';

// Local imports - providers
import authProvider from './providers/authProvider';
import dataProvider from './providers/dataProvider';

// Local imports - components
import ApiDocumentation from './components/ApiDocumentation';
import { OktaLogin, OktaCallback } from './components/Auth';
import { ConfigVersionCreate, ConfigVersionList } from './components/configVersions';
import CustomLayout from './components/CustomLayout';
import CustomLogin from './components/CustomLogin';
import { DashboardResources, pluginResourceProps } from '@src/types';
import PartnerSelect from './components/PartnerSelect';
import { PluginEdit } from './components/plugins';
import { Profile } from './profiles';
import { slateInkTheme } from './theme/slateInkTheme';
import WelcomeDashboard from './components/WelcomeDashboard';
import { CatalogItemList, CatalogItemEdit, CatalogFieldsMappingUpload, CatalogRulesPage, VariantSkuEdit } from './components/catalog';
import { RepairsList, RepairEdit } from './components/repairs';
import {
  pricingRulesResource,
  conditionDiscountsResource,
  itemAdjustedPricesResource,
  PricingRulesHistory,
  ConditionDiscountsHistory,
  ResalePricingOverview,
  RoundingRulesPage,
} from './resalePricing';
import repairPricingResource from './repairPricing';
import shopifyRedirectsResource from './shopifyRedirects';
import shopifySync from './shopifySync';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Okta SSO — /sso avoids conflicting with react-admin's /login redirect */}
        <Route path="/sso" element={<OktaLogin />} />
        <Route path="/authorization-code/callback" element={<OktaCallback />} />

        <Route
          path="*"
          element={
            <Admin
              authProvider={authProvider}
              dataProvider={dataProvider}
              layout={CustomLayout}
              theme={slateInkTheme}
              darkTheme={null}
              title="Trove Partner Dashboard"
              loginPage={CustomLogin}
              dashboard={WelcomeDashboard}
            >
              {/* Define catalog resources for react-admin data layer */}
              <Resource
                name={DashboardResources.CatalogItems}
                list={CatalogItemList}
                edit={CatalogItemEdit}
                recordRepresentation="title"
                options={{ label: 'Catalog Item' }}
              />
              <Resource
                name={DashboardResources.VariantSkus}
                edit={VariantSkuEdit}
                recordRepresentation="sku"
                options={{ label: 'Variant SKU' }}
              />
              {pluginResourceProps.map(({ name, options }) =>
                <Resource
                  key={name}
                  name={name}
                  create={<ConfigVersionCreate label={options?.label || ''} />}
                  edit={<PluginEdit label={options?.label || ''} />}
                  list={<ConfigVersionList label={options?.label || ''} />}
                  options={options}
                />)}
              <Resource
                name={DashboardResources.Repairs}
                list={RepairsList}
                edit={RepairEdit}
                recordRepresentation="uuid"
                options={{ label: 'Repairs' }}
              />
              <Resource {...pricingRulesResource}>
                <Route path="history" element={<PricingRulesHistory />} />
              </Resource>
              <Resource {...conditionDiscountsResource}>
                <Route path="history" element={<ConditionDiscountsHistory />} />
              </Resource>
              <Resource {...itemAdjustedPricesResource} />
              <Resource {...repairPricingResource} />
              <Resource {...shopifyRedirectsResource} />
              <Resource {...shopifySync} />

              <CustomRoutes>
                {/* Partner Selection - shows after login before main app */}
                <Route path="/select-partner" element={<PartnerSelect />} />
                {/* Resale Pricing tabs without a backing resource. Overview lives at the
                    section root (/resalePricing); the resource tabs (pricingRules, …)
                    register their own routes via <Resource> above. */}
                <Route
                  path="/resalePricing"
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.PricingRules} action="list" accessDenied={<AccessDenied />}>
                        <ResalePricingOverview />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                {/* Alias — the Overview tab lives at the section root */}
                <Route path="/resalePricing/overview" element={<Navigate to="/resalePricing" replace />} />
                <Route
                  path="/resalePricing/roundingRules"
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.PricingRules} action="list" accessDenied={<AccessDenied />}>
                        <RoundingRulesPage />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                {/* Catalog Rules - if/then rules for catalog records */}
                <Route
                  path="/catalog-rules"
                  element={
                    <Authenticated>
                      <CatalogRulesPage />
                    </Authenticated>
                  }
                />
                {/* Catalog Fields Mapping Upload - bulk upload display type mappings */}
                <Route
                  path="/catalog-fields-mapping-upload"
                  element={
                    <Authenticated>
                      <CatalogFieldsMappingUpload />
                    </Authenticated>
                  }
                />
                {/* User Profile */}
                <Route
                  path="/profile"
                  element={
                    <Authenticated>
                      <Profile />
                    </Authenticated>
                  }
                />
                {/* API Documentation for developers */}
                <Route
                  path="/devtools/api-docs"
                  element={
                    <Authenticated>
                      <CanAccess resource="devtools/api-docs" action="show" accessDenied={<AccessDenied />}>
                        <ApiDocumentation />
                      </CanAccess>
                    </Authenticated>
                  }
                />
              </CustomRoutes>
            </Admin>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
